import { MapLocation } from 'src/app/alert/models';
import { AlertDefaults, LatLng } from 'src/app/hickory-api';
import { deepCopy } from 'src/app/shared/model/utility';
import { AlertLocationService } from '../../alert-location/alert-location.service';
import { AlertModelService } from '../alert-model.service';
import { AlertFields } from './alert-fields-helper';
import { from as rxFrom } from 'rxjs';
import { IncidentEventDetailsViewModel } from 'src/app/boxwood-api';

export class AlertLocationHelper {
  static isValidCoords(point: LatLng): boolean {
    return (point?.lat || 0) !== 0 && (point?.lng || 0) !== 0;
  }

  static postProcess(service: AlertModelService, alert: AlertDefaults = service.alertSettings, locationService: AlertLocationService = service.locationService) {
    // set the mapLocation and lookup the text address if a location was supplied
    if (this.isValidCoords(alert?.location?.point)) {
      const coords: google.maps.LatLngLiteral = { lat: alert.location.point.lat, lng: alert.location.point.lng, }
      service.state.mapLocation = new MapLocation().withLatLngLiteral(coords);
      service.mapLocation.fromTemplate = true;
      service.mapLocation.confirmed = true;
      this.lookupLocationAddress(service, locationService);
    } else if (!service.mapLocation?.isValidCoords && !!service.findField(AlertFields.LOCATION, alert?.fields)) {
      // else if we don't have a location...
      if (service.isEditingTemplate) {
        this.detectCurrentPosition(service, locationService);
      } else {
        this.lookupAdminAssetLocation(service, locationService);
      }
    }

    if (alert?.location?.areas) {
      alert.location.areas = deepCopy(alert.location.areas);
    }
  }

  static detectCurrentPosition(service: AlertModelService, locationService: AlertLocationService) {
    if (!locationService) return;
    // try to detect the user's current location via the browser's location service
    const rx$ = rxFrom(locationService.detectCurrentPosition());
    service.subscription.add(rx$.subscribe(coords => {
      if (this.isValidCoords(coords)) {
        service.state.mapLocation = new MapLocation().withLatLngLiteral(coords);
        this.lookupLocationAddress(service, locationService);
      }
    }, _err => {
      // handle this error?
    }));
  }

  static lookupAdminAssetLocation(service: AlertModelService, locationService: AlertLocationService) {
    if (!locationService) return;
    const adminId = service.callCenterReporter?.id || service.currentUser?.id || -1;
    const rx$ = rxFrom(locationService.getAdminAssetLocation(adminId));
    service.subscription.add(rx$.subscribe(assetLocation => {
      // use the user's asset location (if available)
      service.state.mapLocation = assetLocation;
      service.notifyFieldUpdated(AlertFields.LOCATION);
    }, _ => {
      // fallback to browser's location detection
      this.detectCurrentPosition(service, locationService);
    }));
  }

  static lookupLocationAddress(service: AlertModelService, locationService: AlertLocationService) {
    if (!locationService) return;
    if (service.mapLocation.isValidCoords) {
      const rx$ = rxFrom(locationService.lookupAddress(service.mapLocation.coords));
      service.subscription.add(rx$.subscribe(textAddress => {
        if (service.mapLocation) {
          service.mapLocation.textAddress = textAddress;
          service.notifyFieldUpdated(AlertFields.LOCATION);
        }
      }, _err => {
        // handle this error?
      }));
    }
  }

  static preparePreview(service: AlertModelService, preview: AlertDefaults) {
    if (!!preview && service.mapLocation?.isValidCoords && service.mapLocation?.confirmed) {
      preview.location = deepCopy(service.alertSettings.location || {});
      preview.location.point = deepCopy(service.mapLocation.coords);
    }

    if (!!preview && service.alertSettings.location?.areas) {
      preview.location.areas = deepCopy(service.alertSettings.location?.areas || []);
    }
  }

  static locationAddressForPreview(service: AlertModelService): string {
    if (service.mapLocation?.isValidAddress && service.mapLocation?.confirmed) {
      return service.mapLocation.textAddress;
    } else {
      return null;
    }
  }

  static assetNameForPreview(service: AlertModelService): string {
    if (!!service.mapLocation?.assetName && service.mapLocation?.confirmed) {
      return service.mapLocation.assetName;
    } else {
      return null;
    }
  }

  static setFacilitiesFromIncidentEvent(service: AlertModelService, event: IncidentEventDetailsViewModel) {
    const alert = service.alertSettings;
    if (!alert) return;
    if (event?.facilities?.length) {
      if (!alert.location) alert.location = {};
      alert.location.facilities = event.facilities;
      service.notifyFieldUpdated(AlertFields.FACILITIES);
    }
  }
}
